import { useEffect, useState } from "react";
import { Box, Button, Flex, Grid, HStack, IconButton, Input, Stack, Text } from "@chakra-ui/react";
import { useCreate, useDelete, useList, useNotification, useOne } from "@refinedev/core";
import { useForm } from "react-hook-form";
import type { ReactNode } from "react";
import { z } from "zod";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Activity, ArrowRight, BrainCircuit, Clock3, GitCompare, History, Layers3, Play, Search, Server, Settings2, ShieldCheck, Trash2, TrendingUp } from "lucide-react";
import { AnnualPerformanceChart, DrawdownMiniChart, EquityChart } from "../components/charts";
import { AppSelect } from "../components/app-select";
import { PopConfirm } from "../components/pop-confirm";
import { DataTable, type Column } from "../components/tables";
import { MetricCard, Money, PageHeader, Panel, Percent, StatusPill } from "../components/ui";
import type { BacktestRecord, BotRecord, StrategyRecord } from "../types/domain";

type StrategySettingView = { key: string; label: string; defaultValue: unknown; description?: string; min?: number; max?: number; step?: number; requiresRestart?: boolean };
type StrategyView = StrategyRecord & { settings?: StrategySettingView[]; defaultConfig?: Record<string, unknown>; supportedTimeframes?: string[] };
type BacktestView = BacktestRecord & { initialEquity: number; endingEquity: number; completedYearsCagr: number; reportMtmMaxDrawdown: number; annualResults?: Array<{ id: number; year: number; strategyReturn: number }>; sessionResults?: Array<{ id: number; sessionDay: string; startEquity: number; endEquity: number; strategyReturn: number; maxActualLeverageAtEvents: number }>; summary?: Record<string, any>; dataQuality?: Record<string, any>; costModel?: Record<string, any> };

export function StrategiesPage() {
  const { result, query } = useList<StrategyView>({ resource: "strategies", pagination: { currentPage: 1, pageSize: 50 } });
  const rows = result.data ?? [];
  return (
    <Stack gap="5">
      <PageHeader
        title="Strategies"
        description="Production strategy registry, execution architecture and server-owned parameters."
        actions={<><StatusPill tone="positive">{rows.length} REGISTERED</StatusPill><Button asChild size="sm" bg="brand.500" color="white"><Link to="/research/backtests/create"><Box as={Play} boxSize="4"/>Run backtest</Link></Button></>}
      />
      <Flex bg="rgba(99,102,241,.07)" borderWidth="1px" borderColor="rgba(99,102,241,.22)" borderRadius="4px" px={{base:"4",md:"5"}} py="3.5" gap="3" align={{base:"start",md:"center"}} direction={{base:"column",md:"row"}}>
        <Flex align="center" gap="2.5" flex="1"><Flex boxSize="8" borderRadius="4px" bg="rgba(99,102,241,.15)" color="brand.400" align="center" justify="center" flexShrink="0"><Box as={ShieldCheck} boxSize="4.5"/></Flex><Box><Text fontSize="sm" fontWeight="700">Server-authoritative configuration</Text><Text fontSize="xs" color="fg.muted">These values come directly from the running strategy registry. Changes are validated by the server and require a bot restart.</Text></Box></Flex>
        <HStack gap="2"><StatusPill tone="neutral">XAUUSD</StatusPill><StatusPill tone="neutral">SHARED KERNEL</StatusPill></HStack>
      </Flex>
      <Stack gap="4">
        {rows.map((strategy) => <StrategyCard key={strategy.id} strategy={strategy}/>) }
        {rows.length === 0 && <Panel><Text color="fg.muted" fontSize="sm">{query.isLoading ? "Loading strategy registry…" : "No registered strategies"}</Text></Panel>}
      </Stack>
    </Stack>
  );
}

function StrategyCard({ strategy }: { strategy: StrategyView }) {
  const isThe5ers = strategy.id === "xau-multi-edge-3-the5ers-v1";
  const isMultiEdge3 = strategy.id === "xau-multi-edge-3-v1" || isThe5ers;
  const isMulti = strategy.id === "xau-multi-edge-portfolio-v1" || isMultiEdge3;
  const isRegime = strategy.id === "xau-regime-consensus-session-v1";
  const isNearMiss = strategy.id === "xau-near-miss-range-v1";
  const Icon = isMulti ? Layers3 : Activity;
  const title = isThe5ers ? "XAU Multi-Edge 3 · The5ers High Stakes" : isMultiEdge3 ? "XAU Multi-Edge 3" : isMulti ? "XAU Multi-Edge Portfolio" : isRegime ? "XAU Regime Consensus Session" : isNearMiss ? "XAU Near-Miss Range" : "XAU Session Momentum";
  const subtitle = isThe5ers ? "Regime Consensus + Near-Miss · prop-firm sizing and account-wide rules" : isMultiEdge3 ? "Regime Consensus + Near-Miss; causal P3 is observation-only" : strategy.id === "xau-multi-edge-portfolio-v1" ? "Momentum quality tiers + Range Session Frequency sleeve" : isRegime ? "Causal regime ensemble with momentum as input only" : isNearMiss ? "Range-only momentum near-miss sleeve" : "Causal intraday momentum with deterministic risk exits";
  const legacyDescription = isMulti
    ? "Runs both sleeves on finalized M5 data and sends one aggregate target to the broker."
    : "Measures the finalized 07:30–08:30 UTC move, enters once per session and exits on the scheduled close or adverse-excursion trigger.";
  const description = isMultiEdge3
    ? "Builds finalized M15 regime features from real M5 candles, observes session momentum without trading it directly, then sends one aggregate portfolio target."
    : isRegime
      ? "Votes over the fixed regime-rule ensemble at the session boundary and enters only when participation and agreement pass."
      : isNearMiss
        ? "Uses the causal session observation only inside the researched near-miss bands and when its P16 range gates agree."
        : legacyDescription;
  const accent = isMulti ? "#a78bfa" : "#60a5fa";
  const softAccent = isMulti ? "rgba(167,139,250,.11)" : "rgba(96,165,250,.11)";
  const settings = strategy.settings ?? [];
  const groups = isMultiEdge3
    ? [
        { title: "Momentum inputs", icon: Activity, items: settings.filter((item) => item.key.startsWith("momentum")) },
        { title: "Regime Consensus sleeve", icon: BrainCircuit, items: settings.filter((item) => item.key.startsWith("regime")) },
        { title: "Predictive P16 sleeve", icon: BrainCircuit, items: settings.filter((item) => item.key.startsWith("p16")) },
        { title: "Near-Miss sleeve", icon: Activity, items: settings.filter((item) => item.key.startsWith("near")) },
        { title: "Portfolio controls", icon: ShieldCheck, items: settings.filter((item) => !/^(momentum|regime|p16|near)/.test(item.key)) },
      ]
    : isMulti
    ? [
        { title: "Momentum sleeve", icon: Activity, items: settings.filter((item) => item.key.startsWith("momentum")) },
        { title: "Range Session Frequency sleeve", icon: BrainCircuit, items: settings.filter((item) => item.key.startsWith("frequency")) },
        { title: "Portfolio controls", icon: ShieldCheck, items: settings.filter((item) => !item.key.startsWith("momentum") && !item.key.startsWith("frequency")) },
      ]
    : [{ title: "Core parameters", icon: Settings2, items: settings }];
  const timeframe = strategy.supportedTimeframes?.join(" · ") || (isMulti ? "5m" : "15m");
  const primaryMetric = isMulti
    ? { label: "Aggregate cap", value: formatStrategyValue("maxAggregateExposure", strategy.defaultConfig?.maxAggregateExposure ?? 14) }
    : { label: "Target exposure", value: formatStrategyValue("targetLeverage", strategy.defaultConfig?.targetLeverage ?? 3.5) };
  return (
    <Box bg="bg.panel" borderWidth="1px" borderColor="border.default" borderRadius="panel" overflow="hidden">
      <Box h="3px" bg={accent}/>
      <Grid templateColumns={{base:"1fr",xl:"340px minmax(0,1fr)"}}>
        <Stack p={{base:"4",md:"5"}} gap="4" borderRightWidth={{base:"0",xl:"1px"}} borderBottomWidth={{base:"1px",xl:"0"}} borderColor="border.default" background={`linear-gradient(145deg, ${softAccent}, transparent 62%)`}>
          <Flex align="start" justify="space-between" gap="3">
            <Flex boxSize="10" align="center" justify="center" borderRadius="4px" bg={softAccent} color={accent} flexShrink="0"><Box as={Icon} boxSize="5"/></Flex>
            <StatusPill tone={isMulti ? "predictive" : "info"}>{isMulti ? "MULTI-SLEEVE" : isRegime ? "REGIME" : isNearMiss ? "NEAR-MISS" : "MOMENTUM"}</StatusPill>
          </Flex>
          <Box>
            <Text fontSize="lg" lineHeight="1.2" fontWeight="800">{title}</Text>
            <Text mt="1.5" fontSize="xs" fontWeight="650" color={accent}>{subtitle}</Text>
            <Text mt="3" fontSize="sm" lineHeight="1.65" color="fg.muted">{description}</Text>
          </Box>
          <HStack gap="2" flexWrap="wrap"><StatusPill tone="neutral">FOREX</StatusPill><StatusPill tone="neutral">{timeframe.toUpperCase()}</StatusPill><StatusPill tone="positive">REGISTERED</StatusPill></HStack>
          <Text fontFamily="mono" fontSize="10px" color="fg.subtle" overflowWrap="anywhere">{strategy.id}</Text>
          <Button asChild size="sm" variant="outline" alignSelf="start"><Link to={`/research/backtests/create?strategy=${encodeURIComponent(strategy.id)}`}><Box as={Play} boxSize="3.5"/>Backtest this strategy</Link></Button>
        </Stack>
        <Stack p={{base:"4",md:"5"}} gap="5" minW="0">
          <Grid templateColumns={{base:"1fr 1fr",md:"repeat(3,1fr)"}} gap="2.5">
            <StrategyFact icon={Clock3} label="Timeframe" value={timeframe.toUpperCase()}/>
            <StrategyFact icon={Settings2} label="Editable parameters" value={String(settings.length)}/>
            <StrategyFact icon={Server} label={primaryMetric.label} value={primaryMetric.value}/>
          </Grid>
          {groups.filter((group) => group.items.length > 0).map((group) => (
            <Box key={group.title}>
              <Flex align="center" gap="2" mb="2.5"><Box as={group.icon} boxSize="4" color={accent}/><Text fontSize="xs" fontWeight="750" letterSpacing=".02em">{group.title}</Text><Box h="1px" bg="border.subtle" flex="1"/></Flex>
              <Grid templateColumns={{base:"1fr",md:"repeat(2,minmax(0,1fr))",lg:"repeat(3,minmax(0,1fr))"}} gap="2">
                {group.items.map((setting) => <StrategySetting key={setting.key} setting={setting}/>) }
              </Grid>
            </Box>
          ))}
        </Stack>
      </Grid>
    </Box>
  );
}

function StrategyFact({ icon, label, value }: { icon: typeof Clock3; label: string; value: string }) {
  return <Flex bg="bg.subtle" borderRadius="4px" p="3" gap="2.5" align="center" minW="0"><Flex boxSize="7" borderRadius="4px" bg="bg.elevated" color="fg.muted" align="center" justify="center" flexShrink="0"><Box as={icon} boxSize="3.5"/></Flex><Box minW="0"><Text fontSize="9px" textTransform="uppercase" letterSpacing=".08em" color="fg.subtle" fontWeight="700">{label}</Text><Text mt=".5" fontFamily="mono" fontSize="sm" fontWeight="800" truncate>{value}</Text></Box></Flex>;
}

function StrategySetting({ setting }: { setting: StrategySettingView }) {
  return <Flex borderWidth="1px" borderColor="border.subtle" borderRadius="4px" px="3" py="2.5" gap="3" align="center" justify="space-between" minW="0"><Box minW="0"><Text fontSize="xs" fontWeight="650" truncate title={setting.label}>{setting.label}</Text><Text fontSize="9px" color="fg.subtle" fontFamily="mono" truncate>{setting.key}</Text></Box><Box bg="bg.subtle" borderRadius="4px" px="2" py="1" flexShrink="0"><Text fontFamily="mono" fontSize="xs" fontWeight="800">{formatStrategyValue(setting.key, setting.defaultValue)}</Text></Box></Flex>;
}

function formatStrategyValue(key: string, value: unknown): string {
  if (typeof value !== "number") return String(value);
  if (/Exposure|Leverage/i.test(key)) return `${value}×`;
  if (/Threshold|TriggerPct|Momentum/i.test(key)) return `${(value * 100).toFixed(value * 100 < 1 ? 2 : 0)}%`;
  return String(value);
}

export function BacktestsPage() {
  const [search, setSearch] = useState("");
  const [strategyFilter, setStrategyFilter] = useState("");
  const { result, query } = useList<BacktestRecord>({ resource: "backtests", pagination: { currentPage: 1, pageSize: 10_000 } });
  const { mutate: deleteRun } = useDelete();
  const { open } = useNotification();
  const allRows = result.data ?? [];
  const rows = allRows.filter((row) =>
    `${row.name} ${row.strategy} ${row.period}`.toLowerCase().includes(search.toLowerCase()) &&
    (!strategyFilter || row.strategy === strategyFilter),
  );
  const strategyOptions = Array.from(
    new Map(allRows.map((row) => [row.strategy, row.name])).entries(),
  ).map(([value, label]) => ({ value, label }));
  const hasFilters = Boolean(search || strategyFilter);
  const removeRun = (row: BacktestRecord) =>
    new Promise<void>((resolve, reject) => {
      deleteRun(
        { resource: "backtests", id: row.id },
        {
          onSuccess: () => {
            open?.({
              type: "success",
              message: "Backtest deleted",
              description: `${row.strategy} · ${row.period}`,
            });
            void query.refetch();
            resolve();
          },
          onError: (error) => {
            open?.({
              type: "error",
              message: "Could not delete backtest",
              description: error.message,
            });
            reject(error);
          },
        },
      );
    });
  const cols: Column<BacktestRecord>[] = [
    { key: "name", header: "Run", render: (row) => <Box><Link to={`/research/backtests/${row.id}`}><Text fontWeight="750">{row.name}</Text></Link><Text fontSize="10px" color="fg.muted">{row.strategy}</Text></Box> },
    { key: "period", header: "Period", render: (row) => <Text whiteSpace="nowrap">{row.period}</Text> },
    { key: "status", header: "Status", render: (row) => <StatusPill tone={row.status === "COMPLETED" ? "positive" : "warning"}>{row.status}</StatusPill> },
    { key: "returnPct", header: "Return", numeric: true, render: (row) => <Percent value={row.returnPct}/> },
    { key: "cagr", header: "CAGR", numeric: true, render: (row) => <Percent value={row.cagr}/> },
    { key: "maxDrawdown", header: "Max DD", numeric: true, render: (row) => <Text fontFamily="mono">-{row.maxDrawdown.toFixed(2)}%</Text> },
    { key: "trades", header: "Order events", numeric: true },
    { key: "createdAt", header: "Created", hideBelow: "lg", render: (row) => <Text color="fg.muted">{row.createdAt}</Text> },
    {
      key: "actions",
      header: "Actions",
      numeric: true,
      mobilePriority: "primary",
      render: (row) => (
        <PopConfirm
          title="Delete this backtest?"
          description={
            <Stack gap="1">
              <Text color="fg.default" fontWeight="700">
                {row.name}
              </Text>
              <Text fontFamily="mono" fontSize="10px">
                {row.period}
              </Text>
              <Text pt="1">
                This persisted run and its stored results will be permanently removed.
              </Text>
            </Stack>
          }
          confirmLabel="Delete backtest"
          confirmAriaLabel={`Confirm delete backtest ${row.name}`}
          cancelAriaLabel={`Cancel delete backtest ${row.name}`}
          onConfirm={() => removeRun(row)}
          trigger={
            <IconButton
              aria-label={`Delete backtest ${row.name}`}
              title="Delete backtest"
              size="xs"
              variant="ghost"
              color="status.negative"
            >
              <Box as={Trash2} boxSize="3.5" />
            </IconButton>
          }
        />
      ),
    },
  ];
  return (
    <Stack gap="5">
      <PageHeader
        title="Backtests"
        description="Persisted results for XAU Momentum (15m) and Multi-Edge (5m)."
        actions={
          <>
            <StatusPill tone="info">{allRows.length} PERSISTED</StatusPill>
            <Button asChild variant="outline" size="sm">
              <Link to="/research/compare">
                <Box as={GitCompare} boxSize="4" />
                Compare runs
              </Link>
            </Button>
            <Button asChild size="sm" bg="brand.500" color="white">
              <Link to="/research/backtests/create">
                <Box as={Play} boxSize="4" />
                Run backtest
              </Link>
            </Button>
          </>
        }
      />
      <Panel
        icon={Search}
        title="Find a backtest"
        action={
          hasFilters ? (
            <Button
              size="xs"
              variant="ghost"
              onClick={() => {
                setSearch("");
                setStrategyFilter("");
              }}
            >
              Clear filters
            </Button>
          ) : undefined
        }
        p="4"
      >
        <Grid
          templateColumns={{ base: "1fr", md: "minmax(0,1fr) 240px" }}
          gap="3"
        >
          <HStack
            bg="bg.subtle"
            px="3"
            borderRadius="4px"
            borderWidth="1px"
            borderColor="border.default"
            _focusWithin={{ borderColor: "brand.500" }}
          >
            <Box as={Search} boxSize="4" color="fg.muted" />
            <Input
              aria-label="Search backtests"
              border="0"
              bg="transparent"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search run, strategy or period"
            />
          </HStack>
          <AppSelect
            ariaLabel="Filter backtests by strategy"
            value={strategyFilter}
            options={[
              { value: "", label: "All strategies" },
              ...strategyOptions,
            ]}
            onValueChange={setStrategyFilter}
          />
        </Grid>
      </Panel>
      <Panel
        icon={History}
        title="Backtest history"
        action={<StatusPill>{rows.length} RESULTS</StatusPill>}
      >
        <DataTable
          rows={rows}
          columns={cols}
          rowLabel="runs"
          pageSize={5}
          pageSizeOptions={[5, 10, 25]}
          empty={query.isLoading ? "Loading backtests…" : "No backtests match the current filters"}
        />
      </Panel>
    </Stack>
  );
}

export function BacktestDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { result: run, query } = useOne<BacktestView>({ resource: "backtests", id });
  const { mutate: deleteRun } = useDelete();
  const { open } = useNotification();
  if (!run) return <Panel><Text color="fg.muted">{query.isLoading ? "Loading backtest…" : "Backtest not found"}</Text></Panel>;
  let hwm = 0;
  const equity = (run.sessionResults ?? []).sort((a, b) => a.sessionDay.localeCompare(b.sessionDay)).map((session) => {
    hwm = Math.max(hwm, session.endEquity);
    return { time: session.sessionDay, equity: session.endEquity, hwm, drawdown: hwm > 0 ? (session.endEquity / hwm - 1) * 100 : 0, exposure: session.maxActualLeverageAtEvents };
  });
  const annual = (run.annualResults ?? []).sort((a, b) => a.year - b.year).map((row) => ({ year: row.year, portfolio: row.strategyReturn * 100 }));
  const annualRows = annual.map((row) => ({ id: row.year, year: row.year, ret: row.portfolio }));
  const removeRun = () =>
    new Promise<void>((resolve, reject) => {
      deleteRun(
        { resource: "backtests", id: run.id },
        {
          onSuccess: () => {
            open?.({ type: "success", message: "Backtest deleted" });
            navigate("/research/backtests");
            resolve();
          },
          onError: (error) => {
            open?.({
              type: "error",
              message: "Could not delete backtest",
              description: error.message,
            });
            reject(error);
          },
        },
      );
    });
  return <Stack gap="5"><PageHeader title={run.name} description={`${run.symbol} · ${run.period}`} back={{ label: "Backtests", to: "/research/backtests" }} actions={<><Button asChild variant="outline" size="sm"><Link to="/research/compare"><Box as={GitCompare} boxSize="4"/>Compare</Link></Button><PopConfirm title="Delete this backtest?" description={<Stack gap="1"><Text color="fg.default" fontWeight="700">{run.name}</Text><Text fontFamily="mono" fontSize="10px">{run.period}</Text><Text pt="1">This persisted run and its stored results will be permanently removed.</Text></Stack>} confirmLabel="Delete backtest" confirmAriaLabel={`Confirm delete backtest ${run.name}`} cancelAriaLabel={`Cancel delete backtest ${run.name}`} onConfirm={removeRun} trigger={<Button variant="outline" size="sm" color="status.negative"><Box as={Trash2} boxSize="3.5"/>Delete</Button>}/></>}/><Grid templateColumns={{base:"1fr 1fr",lg:"repeat(4,1fr)"}} gap="3"><MetricCard label="Total return" value={<Percent value={run.returnPct}/>} positive={run.returnPct >= 0}/><MetricCard label="Completed-years CAGR" value={<Percent value={run.cagr}/>} positive={run.cagr >= 0}/><MetricCard label="Max drawdown" value={`-${run.maxDrawdown.toFixed(2)}%`} detail="Persisted mark-to-market result"/><MetricCard label="Order events" value={String(run.trades)} detail="Executed momentum orders"/></Grid><Grid templateColumns={{base:"1fr",xl:"minmax(0,1.4fr) minmax(340px,.6fr)"}} gap="4"><Panel title="Equity curve"><EquityChart data={equity}/></Panel><Panel title="Run metadata"><Stack gap="3"><Meta label="Initial equity" value={`$${run.initialEquity.toLocaleString()}`}/><Meta label="Ending equity" value={`$${run.endingEquity.toLocaleString()}`}/><Meta label="Cost model" value={JSON.stringify(run.costModel ?? {})}/><Meta label="Data quality" value={JSON.stringify(run.dataQuality ?? {})}/></Stack></Panel></Grid><Grid templateColumns={{base:"1fr",lg:"1fr 1fr"}} gap="4"><Panel title="Annual returns"><AnnualPerformanceChart data={annual}/></Panel><Panel title="Drawdown"><DrawdownMiniChart data={equity}/></Panel></Grid><Panel title="Annual results"><DataTable rows={annualRows} columns={[{key:"year",header:"Year"},{key:"ret",header:"Return",numeric:true,render:(row:any)=><Percent value={row.ret}/>}]} rowLabel="annual results" empty="No annual results"/></Panel></Stack>;
}

function Meta({ label, value }: { label: string; value: string }) {
  return <Box p="3" bg="bg.subtle" borderRadius="4px"><Text fontSize="10px" color="fg.muted">{label}</Text><Text mt="1" fontSize="10px" fontFamily="mono" overflowWrap="anywhere">{value}</Text></Box>;
}

export function CompareRunsPage() {
  const [leftId, setLeftId] = useState("");
  const [rightId, setRightId] = useState("");
  const { result, query } = useList<BacktestRecord>({ resource: "backtests", pagination: { currentPage: 1, pageSize: 10_000 }, sorters: [{ field: "createdAt", order: "desc" }] });
  const rows = result.data ?? [];
  const a = rows.find((row) => row.id === leftId) ?? rows[0];
  const b = rows.find((row) => row.id === rightId && row.id !== a?.id) ?? rows.find((row) => row.id !== a?.id);
  if (!a || !b) return <Stack gap="5"><PageHeader title="Compare Backtests" description="Choose any two persisted strategy runs." back={{ label: "Backtests", to: "/research/backtests" }}/><Panel><Text color="fg.muted" fontSize="xs">{query.isLoading ? "Loading runs…" : "At least two persisted backtests are required."}</Text></Panel></Stack>;

  const options = rows.map((row) => ({
    value: row.id,
    label: `${row.name} · ${row.period} · ${row.createdAt}`,
  }));
  const returnA = roundComparisonMetric(a.returnPct);
  const returnB = roundComparisonMetric(b.returnPct);
  const cagrA = roundComparisonMetric(a.cagr);
  const cagrB = roundComparisonMetric(b.cagr);
  const drawdownA = roundComparisonMetric(a.maxDrawdown);
  const drawdownB = roundComparisonMetric(b.maxDrawdown);
  const returnDelta = returnA - returnB;
  const cagrDelta = cagrA - cagrB;
  const drawdownDelta = drawdownA - drawdownB;
  const eventDelta = a.trades - b.trades;
  return (
    <Stack gap="5">
      <PageHeader
        title="Compare Backtests"
        description="Select two persisted runs and compare identical stored performance metrics."
        back={{ label: "Backtests", to: "/research/backtests" }}
        actions={<StatusPill tone="info">{rows.length} RUNS AVAILABLE</StatusPill>}
      />
      <Panel
        icon={GitCompare}
        title="Choose runs"
        action={(leftId || rightId) ? (
          <Button size="xs" variant="ghost" onClick={() => { setLeftId(""); setRightId(""); }}>
            Use latest two
          </Button>
        ) : <StatusPill>LATEST TWO</StatusPill>}
      >
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="4">
          <Stack gap="2">
            <HStack gap="2">
              <StatusPill tone="info">RUN A</StatusPill>
              <Text fontSize="10px" color="fg.muted">Baseline</Text>
            </HStack>
            <AppSelect
              ariaLabel="Select backtest for Run A"
              value={a.id}
              options={options.map((option) => ({ ...option, disabled: option.value === b.id }))}
              onValueChange={(value) => {
                if (!rightId) setRightId(b.id);
                setLeftId(value);
              }}
            />
          </Stack>
          <Stack gap="2">
            <HStack gap="2">
              <StatusPill tone="predictive">RUN B</StatusPill>
              <Text fontSize="10px" color="fg.muted">Challenger</Text>
            </HStack>
            <AppSelect
              ariaLabel="Select backtest for Run B"
              value={b.id}
              options={options.map((option) => ({ ...option, disabled: option.value === a.id }))}
              onValueChange={(value) => {
                if (!leftId) setLeftId(a.id);
                setRightId(value);
              }}
            />
          </Stack>
        </Grid>
      </Panel>
      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap="4">
        <RunChoice run={a} label="RUN A" tone="info" accent="brand.500" />
        <RunChoice run={b} label="RUN B" tone="predictive" accent="strategy.predictive" />
      </Grid>
      <Grid templateColumns={{ base: "1fr 1fr", xl: "repeat(4,1fr)" }} gap="3">
        <MetricCard icon={TrendingUp} label="Return delta · A − B" value={<DeltaValue value={returnDelta} good={returnDelta === 0 ? undefined : returnDelta > 0} suffix="pp" />} detail={returnDelta === 0 ? "Runs are tied" : "Positive favors Run A"} />
        <MetricCard icon={Activity} label="CAGR delta · A − B" value={<DeltaValue value={cagrDelta} good={cagrDelta === 0 ? undefined : cagrDelta > 0} suffix="pp" />} detail={cagrDelta === 0 ? "Runs are tied" : "Positive favors Run A"} />
        <MetricCard icon={ShieldCheck} label="Drawdown delta · A − B" value={<DeltaValue value={drawdownDelta} good={drawdownDelta === 0 ? undefined : drawdownDelta < 0} suffix="pp" />} detail={drawdownDelta === 0 ? "Runs are tied" : "Lower drawdown is better"} />
        <MetricCard icon={Layers3} label="Order-event delta · A − B" value={<DeltaValue value={eventDelta} suffix="" />} detail="Activity difference only" />
      </Grid>
      <Panel icon={GitCompare} title="Metric breakdown" action={<StatusPill>RUN A VS RUN B</StatusPill>}>
        <Box display={{ base: "none", md: "block" }}>
          <Grid templateColumns="minmax(170px,1fr) 150px 150px 190px" gap="3" px="3" pb="2">
            <ComparisonHeader>Metric</ComparisonHeader>
            <ComparisonHeader>Run A</ComparisonHeader>
            <ComparisonHeader>Run B</ComparisonHeader>
            <ComparisonHeader>Difference</ComparisonHeader>
          </Grid>
        </Box>
        <ComparisonMetricRow label="Total return" a={`${returnA.toFixed(2)}%`} b={`${returnB.toFixed(2)}%`} result={comparisonResult(returnA, returnB, "higher", "pp")} />
        <ComparisonMetricRow label="Completed-years CAGR" a={`${cagrA.toFixed(2)}%`} b={`${cagrB.toFixed(2)}%`} result={comparisonResult(cagrA, cagrB, "higher", "pp")} />
        <ComparisonMetricRow label="Max drawdown" a={`${drawdownA.toFixed(2)}%`} b={`${drawdownB.toFixed(2)}%`} result={comparisonResult(drawdownA, drawdownB, "lower", "pp")} />
        <ComparisonMetricRow label="Order events" a={a.trades.toLocaleString()} b={b.trades.toLocaleString()} result={comparisonResult(a.trades, b.trades, "activity", "events")} />
      </Panel>
    </Stack>
  );
}

function RunChoice({ run, label, tone, accent }: { run: BacktestRecord; label: string; tone: "info" | "predictive"; accent: string }) {
  return (
    <Box borderRadius="4px" borderWidth="1px" borderColor="border.default" bg="bg.panel" overflow="hidden">
      <Box h="3px" bg={accent} />
      <Stack p="4" gap="4">
        <Flex align="start" justify="space-between" gap="3">
          <Box minW="0">
            <HStack gap="2" mb="2"><StatusPill tone={tone}>{label}</StatusPill><StatusPill tone={run.status === "COMPLETED" ? "positive" : run.status === "FAILED" ? "negative" : "warning"}>{run.status}</StatusPill></HStack>
            <Text fontSize="lg" fontWeight="800" truncate>{run.name}</Text>
            <Text mt="1" fontFamily="mono" fontSize="10px" color="fg.subtle" truncate>{run.strategy}</Text>
          </Box>
          <Flex boxSize="34px" flexShrink="0" align="center" justify="center" borderRadius="4px" bg="bg.subtle" color={accent}><Box as={Clock3} boxSize="4" /></Flex>
        </Flex>
        <Text fontSize="xs" color="fg.muted">{run.period} · created {run.createdAt}</Text>
        <Grid templateColumns="repeat(3,1fr)" gap="2">
          <RunMetric label="Return" value={<Percent value={run.returnPct} />} />
          <RunMetric label="CAGR" value={<Percent value={run.cagr} />} />
          <RunMetric label="Max DD" value={`-${run.maxDrawdown.toFixed(2)}%`} />
        </Grid>
        <Button asChild size="sm" variant="outline" alignSelf="start">
          <Link to={`/research/backtests/${run.id}`}>View run <Box as={ArrowRight} boxSize="3.5" /></Link>
        </Button>
      </Stack>
    </Box>
  );
}

function RunMetric({ label, value }: { label: string; value: ReactNode }) {
  return <Box p="3" borderRadius="4px" bg="bg.subtle"><Text fontSize="9px" color="fg.subtle" textTransform="uppercase" letterSpacing=".06em">{label}</Text><Text mt="1" fontFamily="mono" fontSize="sm" fontWeight="800">{value}</Text></Box>;
}

function DeltaValue({ value, good, suffix }: { value: number; good?: boolean; suffix: string }) {
  const signed = `${value > 0 ? "+" : ""}${Number.isInteger(value) ? value : value.toFixed(2)}${suffix ? ` ${suffix}` : ""}`;
  return <Text as="span" fontFamily="mono" color={good === undefined ? "fg.default" : good ? "status.positive" : "status.negative"}>{signed}</Text>;
}

function ComparisonHeader({ children }: { children: ReactNode }) {
  return <Text fontSize="9px" fontWeight="800" color="fg.subtle" textTransform="uppercase" letterSpacing=".06em">{children}</Text>;
}

type ComparisonResult = { text: string; tone: "positive" | "predictive" | "neutral" };

function ComparisonMetricRow({ label, a, b, result }: { label: string; a: string; b: string; result: ComparisonResult }) {
  return (
    <Box py="3" px="3" borderTopWidth="1px" borderColor="border.subtle">
      <Grid display={{ base: "none", md: "grid" }} templateColumns="minmax(170px,1fr) 150px 150px 190px" gap="3" alignItems="center">
        <Text fontSize="xs" fontWeight="700">{label}</Text>
        <Text fontFamily="mono" fontSize="xs" fontWeight="750">{a}</Text>
        <Text fontFamily="mono" fontSize="xs" fontWeight="750">{b}</Text>
        <StatusPill tone={result.tone}>{result.text}</StatusPill>
      </Grid>
      <Stack display={{ base: "flex", md: "none" }} gap="2">
        <Flex justify="space-between" gap="3"><Text fontSize="xs" fontWeight="700">{label}</Text><StatusPill tone={result.tone}>{result.text}</StatusPill></Flex>
        <Grid templateColumns="1fr 1fr" gap="2"><RunMetric label="Run A" value={a} /><RunMetric label="Run B" value={b} /></Grid>
      </Stack>
    </Box>
  );
}

function comparisonResult(a: number, b: number, mode: "higher" | "lower" | "activity", unit: string): ComparisonResult {
  const delta = Math.abs(a - b);
  if (delta < 0.005) return { text: "TIE", tone: "neutral" };
  const runA = mode === "lower" ? a < b : a > b;
  const direction = mode === "activity" ? "more" : mode === "lower" ? "lower" : "higher";
  const formatted = Number.isInteger(delta) ? delta.toLocaleString() : delta.toFixed(2);
  return { text: `RUN ${runA ? "A" : "B"} · ${formatted} ${unit} ${direction}`, tone: runA ? "positive" : "predictive" };
}

function roundComparisonMetric(value: number): number {
  return Number(value.toFixed(2));
}

const backtestSchema = z.object({
  dataPath: z.string().optional(),
  dataTimeBasis: z.enum(["RESEARCH_WALL_CLOCK", "UTC"]),
  from: z.string().min(1),
  to: z.string().min(1),
  initialEquity: z.coerce.number().positive(),
  spread: z.coerce.number().min(0),
  slippage: z.coerce.number().min(0),
  commission: z.coerce.number().min(0),
});
type BacktestFormValues = z.infer<typeof backtestSchema>;

export function BacktestRunnerPage() {
  const { open } = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedStrategy = searchParams.get("strategy");
  const requestedBotId = searchParams.get("botId");
  const supportedStrategies = ["xau-session-momentum-v1", "xau-regime-consensus-session-v1", "xau-multi-edge-portfolio-v1", "xau-near-miss-range-v1", "xau-multi-edge-3-v1", "xau-multi-edge-3-the5ers-v1"];
  const [strategy, setStrategy] = useState(supportedStrategies.includes(requestedStrategy ?? "") ? requestedStrategy! : "xau-session-momentum-v1");
  const { result: mirroredBot } = useOne<BotRecord>({
    resource: "bots",
    id: requestedBotId ?? "",
    queryOptions: { enabled: Boolean(requestedBotId) },
  });
  const { mutate: create, mutation } = useCreate<BacktestView>();
  const { register, handleSubmit, setError, setValue, formState } = useForm<BacktestFormValues>({ defaultValues: { dataPath: "", dataTimeBasis: "RESEARCH_WALL_CLOCK", from: "2021-01-01", to: new Date().toISOString().slice(0, 10), initialEquity: 3000, spread: 0.23, slippage: 0.03, commission: 3.5 } });
  useEffect(() => {
    if (!mirroredBot) return;
    if (supportedStrategies.includes(mirroredBot.strategy)) {
      setStrategy(mirroredBot.strategy);
    }
    if (mirroredBot.equity > 0) setValue("initialEquity", mirroredBot.equity);
  }, [mirroredBot, setValue]);
  const submit = handleSubmit((raw) => {
    const parsed = backtestSchema.safeParse(raw);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) setError(issue.path[0] as keyof BacktestFormValues, { type: "validate", message: issue.message });
      return;
    }
    create({ resource: "backtests", values: { botId: requestedBotId || undefined, strategyId: strategy, dataPath: parsed.data.dataPath?.trim() || undefined, dataTimeBasis: parsed.data.dataTimeBasis, initialEquity: parsed.data.initialEquity, tradeStart: parsed.data.from, reportStart: parsed.data.from, reportEnd: parsed.data.to, spreadUsdPerOz: parsed.data.spread, slippageUsdPerOzPerSide: parsed.data.slippage, commissionUsdPerLotPerSide: parsed.data.commission } }, { onSuccess: (response) => { open?.({ type: "success", message: "Backtest completed", description: "The run and all result rows were persisted." }); navigate(`/research/backtests/${response.data.id}`); }, onError: (error) => open?.({ type: "error", message: "Backtest failed", description: error.message }) });
  });
  const isMultiEdge = strategy === "xau-multi-edge-portfolio-v1" || strategy === "xau-multi-edge-3-v1" || strategy === "xau-multi-edge-3-the5ers-v1" || strategy === "xau-near-miss-range-v1";
  const strategyLabel = strategy === "xau-multi-edge-3-the5ers-v1" ? "The5ers High Stakes" : strategy === "xau-multi-edge-3-v1" ? "Multi-Edge 3" : strategy === "xau-multi-edge-portfolio-v1" ? "Multi-Edge" : strategy === "xau-near-miss-range-v1" ? "Near-Miss" : strategy === "xau-regime-consensus-session-v1" ? "Regime Consensus" : "Momentum";
  return (
    <Stack gap="5">
      <PageHeader
        title={`Run ${strategyLabel} Backtest`}
        description={isMultiEdge ? "Run the selected production M5 strategy with the same causal M5→M15 logic used by its runtime sleeves." : "Execute and persist the selected causal XAU session strategy through the same trading kernel."}
        back={{ label: "Backtests", to: "/research/backtests" }}
        actions={<StatusPill tone="info">{requestedBotId ? "LIVE BOT MIRROR" : isMultiEdge ? "CAUSAL M5 PORTFOLIO" : strategyLabel.toUpperCase()}</StatusPill>}
      />
      <Grid as="form" onSubmit={submit} templateColumns={{base:"1fr",xl:"minmax(0,1fr) 380px"}} gap="4">
        <Stack gap="4">
          <Panel title="Strategy">
            {mirroredBot && <Text mb="3" fontSize="xs" color="fg.muted">Mirroring bot <strong>{mirroredBot.name}</strong>: strategy, timeframe, instrument sizing and safety limits come from its saved configuration.</Text>}
            <Grid templateColumns={{base:"1fr",md:"repeat(2,1fr)",xl:"repeat(3,1fr)"}} gap="3">
              <Button type="button" disabled={Boolean(requestedBotId)} variant={strategy === "xau-session-momentum-v1" ? "solid" : "outline"} onClick={()=>setStrategy("xau-session-momentum-v1")}>Session Momentum · 15m</Button>
              <Button type="button" disabled={Boolean(requestedBotId)} variant={strategy === "xau-regime-consensus-session-v1" ? "solid" : "outline"} onClick={()=>setStrategy("xau-regime-consensus-session-v1")}>Regime Consensus · 15m</Button>
              <Button type="button" disabled={Boolean(requestedBotId)} variant={strategy === "xau-multi-edge-portfolio-v1" ? "solid" : "outline"} onClick={()=>setStrategy("xau-multi-edge-portfolio-v1")}>Multi-Edge · 5m</Button>
              <Button type="button" disabled={Boolean(requestedBotId)} variant={strategy === "xau-near-miss-range-v1" ? "solid" : "outline"} onClick={()=>setStrategy("xau-near-miss-range-v1")}>Near-Miss · 5m</Button>
              <Button type="button" disabled={Boolean(requestedBotId)} variant={strategy === "xau-multi-edge-3-v1" ? "solid" : "outline"} onClick={()=>setStrategy("xau-multi-edge-3-v1")}>Multi-Edge 3 · 5m</Button>
              <Button type="button" disabled={Boolean(requestedBotId)} variant={strategy === "xau-multi-edge-3-the5ers-v1" ? "solid" : "outline"} onClick={()=>setStrategy("xau-multi-edge-3-the5ers-v1")}>The5ers High Stakes · 5m</Button>
            </Grid>
          </Panel>
          <Panel title="Dataset and period">
            <Grid templateColumns={{base:"1fr",md:"1fr 1fr"}} gap="3">
              <BacktestField label="Strategy"><Input value={strategy} readOnly/></BacktestField>
              {isMultiEdge ? <BacktestField label="Real M5 CSV path (optional if configured on server)" error={formState.errors.dataPath?.message}><Input placeholder="Server MULTI_EDGE_MARKET_DATA_PATH" {...register("dataPath")}/></BacktestField> : <BacktestField label="Dataset"><Input value="Server MARKET_DATA_PATH" readOnly/></BacktestField>}
              <BacktestField label="Dataset timestamp basis" error={formState.errors.dataTimeBasis?.message}>
                <Box as="select" h="10" px="3" borderWidth="1px" borderColor="border.default" borderRadius="4px" bg="bg.panel" fontSize="sm" {...register("dataTimeBasis")}>
                  <option value="RESEARCH_WALL_CLOCK">Legacy research wall clock (already EET/EEST)</option>
                  <option value="UTC">True UTC (cTrader → Pepperstone server clock)</option>
                </Box>
              </BacktestField>
              <BacktestField label="Initial equity" error={formState.errors.initialEquity?.message}><Input type="number" {...register("initialEquity")}/></BacktestField>
              <BacktestField label="From" error={formState.errors.from?.message}><Input type="date" {...register("from")}/></BacktestField>
              <BacktestField label="To" error={formState.errors.to?.message}><Input type="date" {...register("to")}/></BacktestField>
            </Grid>
          </Panel>
          <Panel title="Execution costs">
            <Grid templateColumns={{base:"1fr",md:"repeat(3,1fr)"}} gap="3">
              <BacktestField label="Spread"><Input type="number" step="0.01" {...register("spread")}/></BacktestField>
              <BacktestField label="Slippage / side"><Input type="number" step="0.01" {...register("slippage")}/></BacktestField>
              <BacktestField label="Commission"><Input type="number" step="0.1" {...register("commission")}/></BacktestField>
            </Grid>
          </Panel>
        </Stack>
        <Stack gap="4">
          <Panel title="Execution">
            <Text fontSize="xs" color="fg.muted">{requestedBotId ? "The server snapshots this bot's strategy, timeframe, broker lot grid and safety limits. Sub-minimum targets stay flat until account equity can represent them; incomplete or safety-paused runs are rejected." : isMultiEdge ? "The server validates genuine 5m data and persists actual shared-kernel simulator results. Each selected portfolio uses its own registered sleeves; no M5 data is fabricated from M15." : "The server runs the selected 15m session strategy through the trading kernel and simulated broker, then persists daily equity, orders, costs and drawdown."}</Text>
          </Panel>
          <Button type="submit" bg="brand.500" color="white" disabled={formState.isSubmitting || mutation.isPending}><Box as={Play} boxSize="4"/>Run {strategyLabel} Backtest</Button>
          <Button asChild type="button" variant="outline"><Link to="/research/backtests">Cancel</Link></Button>
        </Stack>
      </Grid>
    </Stack>
  );
}

function BacktestField({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return <Box><Text fontSize="10px" color="fg.muted" mb="1.5">{label}</Text>{children}{error && <Text mt="1" fontSize="9px" color="status.negative">{error}</Text>}</Box>;
}
